<!--
# Frontend Vue | Vendor: FairVerify | App: Phoenix | Type: VisitorLeadListTab
# FairVerify Event Solutions (fairverify.de) based on GodMode Core - GodMode.Systems
# Copyright Tim Haymann, FairVerify Event Solutions GmbH.
-->
<template>
  <div class="container py-3 pb-5">
    <div class="row g-3 mb-3">
      <div class="col-6">
        <div class="card shadow-sm h-100">
          <div class="card-body">
            <div class="text-muted small text-uppercase">{{ t('list.leads') }}</div>
            <div class="display-6 fw-bold">{{ totalCount }}</div>
          </div>
        </div>
      </div>
      <div class="col-6">
        <div class="card shadow-sm h-100">
          <div class="card-body d-flex flex-column justify-content-between">
            <div class="text-muted small text-uppercase">{{ t('list.sync') }}</div>
            <div v-if="unsyncedCount > 0" class="d-flex flex-column gap-2">
              <div class="fw-semibold">{{ t('list.unsyncedCount', { count: unsyncedCount }) }}</div>
              <button
                class="btn btn-sm btn-outline-primary rounded-pill align-self-start"
                type="button"
                @click="$emit('sync-pending')"
              >
                {{ t('actions.syncNow') }}
              </button>
            </div>
            <div v-else class="text-success fw-semibold">{{ t('list.allSynced') }}</div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="availableFairs.length > 0" class="d-flex align-items-center gap-2 flex-wrap mb-3">
      <button
        v-for="fair in visibleFairTabs"
        :key="fair.id"
        class="btn btn-sm rounded-pill"
        :class="selectedFairId === fair.id ? 'btn-primary' : 'btn-outline-secondary'"
        type="button"
        @click="$emit('select-fair', fair.id)"
      >
        {{ fairDisplayLabel(fair.id) }}
      </button>
      <button
        v-if="moreFairsAvailable"
        class="btn btn-sm btn-outline-secondary rounded-pill"
        type="button"
        @click="$emit('open-fair-modal')"
      >
        {{ t('list.moreFairs') }}
      </button>
    </div>

    <div v-if="noResults" class="card shadow-sm">
      <div class="card-body text-center py-5">
        <h2 class="h5">{{ t('list.emptyTitle') }}</h2>
        <p class="text-muted mb-0">{{ t('list.emptyText') }}</p>
      </div>
    </div>

    <div v-else class="d-flex flex-column gap-3">
      <article
        v-for="scan in scans"
        :key="scan.id"
        class="card shadow-sm"
        :class="{ 'visitor-lead-card-link': isExhibitorProfileLinkable(scan) }"
        @click="$emit('open-exhibitor-profile', scan)"
      >
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-start gap-3">
            <div class="visitor-lead-card-copy">
              <div class="visitor-lead-title-row">
                <img
                  v-if="scan.logoUrl"
                  :src="scan.logoUrl"
                  alt=""
                  class="visitor-lead-inline-logo"
                >
                <h2 class="h5 mb-1">{{ scanDisplayName(scan) || t('scan.notYetSynced') }}</h2>
              </div>
              <p v-if="scanDisplaySubtitle(scan)" class="text-muted mb-1">
                {{ scanDisplaySubtitle(scan) }}
              </p>
              <p v-if="scanDisplayMeta(scan)" class="text-muted mb-1">{{ scanDisplayMeta(scan) }}</p>
              <p class="text-muted small mb-0">{{ t('scan.scannedAt', { date: formatDate(scan.scannedAt) }) }}</p>
            </div>
            <div class="visitor-lead-card-side">
              <span class="badge rounded-pill text-bg-light text-uppercase">{{ statusLabel(scan.status) }}</span>
              <img
                v-if="scan.logoUrl"
                :src="scan.logoUrl"
                alt=""
                class="visitor-lead-card-logo"
              >
            </div>
          </div>
        </div>
      </article>
    </div>

    <div v-if="totalPages > 1" class="d-flex justify-content-between align-items-center mt-4">
      <button
        class="btn btn-outline-secondary rounded-pill"
        type="button"
        :disabled="currentPage === 1"
        @click="$emit('change-page', -1)"
      >
        {{ t('list.previous') }}
      </button>
      <div class="text-muted small">{{ t('list.page', { page: currentPage, total: totalPages }) }}</div>
      <button
        class="btn btn-outline-secondary rounded-pill"
        type="button"
        :disabled="currentPage === totalPages"
        @click="$emit('change-page', 1)"
      >
        {{ t('list.next') }}
      </button>
    </div>
  </div>
</template>

<script>
import { translateVisitorLead } from '@/components/visitor-lead/i18n.js'

const NO_FAIR_ID = 'OHNE_MESSE'

export default {
  name: 'VisitorLeadListTab',
  props: {
    scans: { type: Array, default: () => [] },
    totalCount: { type: Number, default: 0 },
    noResults: { type: Boolean, default: false },
    unsyncedCount: { type: Number, default: 0 },
    availableFairs: { type: Array, default: () => [] },
    visibleFairTabs: { type: Array, default: () => [] },
    moreFairsAvailable: { type: Boolean, default: false },
    selectedFairId: { type: String, default: '' },
    currentPage: { type: Number, default: 1 },
    totalPages: { type: Number, default: 1 },
    debugEnabled: { type: Boolean, default: false },
    locale: { type: String, default: 'en' },
  },
  methods: {
    t(key, replacements = {}) {
      return translateVisitorLead(key, replacements, this.locale)
    },
    fairDisplayLabel(fairId) {
      return fairId === NO_FAIR_ID ? this.t('scan.noFair') : fairId
    },
    scanDisplayName(scan) {
      const fullName = [scan.firstName, scan.lastName].filter(Boolean).join(' ').trim()
      return fullName || scan.name || scan.companyName || ''
    },
    scanDisplaySubtitle(scan) {
      const hasPersonName = Boolean([scan.firstName, scan.lastName].filter(Boolean).join(' ').trim() || scan.name)

      if (hasPersonName) {
        const segments = [scan.companyName, scan.jobTitle].filter(Boolean)
        return segments.join(' \u00b7 ')
      }

      const exhibitorSegments = []
      if (scan.hall) exhibitorSegments.push(scan.hall)
      if (scan.stand) exhibitorSegments.push(this.t('scan.boothLabel', { stand: scan.stand }))
      if (exhibitorSegments.length) return exhibitorSegments.join(' \u00b7 ')

      if (this.debugEnabled && scan.exhibitorSfid) {
        return scan.exhibitorSfid
      }

      return ''
    },
    scanDisplayMeta(scan) {
      if (!scan.name && !scan.firstName && !scan.lastName && !scan.companyName && !scan.jobTitle) {
        return this.t('scan.unsyncedInfo')
      }

      if (!this.debugEnabled) {
        return ''
      }

      const segments = []
      if (scan.fairId) segments.push(this.fairDisplayLabel(scan.fairId))
      if (scan.code) segments.push(`ID: ${scan.code}`)
      return segments.join(' \u00b7 ')
    },
    formatDate(value) {
      return new Date(value).toLocaleString(this.locale === 'de' ? 'de-DE' : 'en-US')
    },
    statusLabel(status) {
      if (status === 'synced') return this.t('status.synced')
      if (status === 'failed') return this.t('status.failed')
      return this.t('status.pending')
    },
    isExhibitorProfileLinkable(scan) {
      return /^\d+$/.test(String(scan?.exhibitorId || '')) || Boolean(scan?.exhibitorSfid)
    },
  },
}
</script>

<style scoped>
.visitor-lead-card-copy {
  min-width: 0;
  flex: 1 1 auto;
}

.visitor-lead-title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.visitor-lead-title-row .h5 {
  min-width: 0;
}

.visitor-lead-card-side {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.65rem;
}

.visitor-lead-card-logo {
  display: block;
  width: 88px;
  max-height: 34px;
  object-fit: contain;
  object-position: right center;
}

.visitor-lead-inline-logo {
  display: none;
  width: 26px;
  height: 26px;
  object-fit: contain;
  object-position: center;
  flex: 0 0 auto;
}

@media (max-width: 575.98px) {
  .visitor-lead-card-logo {
    display: none;
  }

  .visitor-lead-inline-logo {
    display: block;
  }
}

.visitor-lead-card-link {
  cursor: pointer;
}
</style>
